import Job, { JobScopeItem } from 'jobs/Job'
import JobStore from 'jobs/JobStore'
import Summary from 'jobs/Summary'
import SummaryStore from 'jobs/SummaryStore'
import { SUPPORTED_ENGINE_VERSION } from 'legends/API'
import PublishedLegendStore from 'legends/PublishedLegendStore'
import { action, computed, makeObservable, observable } from 'mobx'
import { task } from 'mobx-task'
import { Id } from 'types/definitions'
import ApplyCouponAsAdminViewModel from '../Job/ApplyCouponAsAdminDialog/ApplyCouponAsAdminViewModel'
import CouponStore from 'billing/CouponStore'
import { AlertFactory } from 'alerts/Alert'
import { PublishedLegend } from 'legends/PublishedLegend'
import { Subscription } from 'rxjs'
import BillingMethod from 'billing/models/BillingMethod'
import { JobStatus } from 'jobs/dtos/job'
import RemoveCouponAsAdminViewModel from '../Job/RemoveCouponAsAdminDialog/RemoveCouponAsAdminViewModel'
import Coupon from 'billing/models/Coupon'
import { formatMoney } from 'utils/money-util'
import { asNumber } from 'utils/number-util'
import { Env } from 'infra/env'

export class JobScopeItemViewModel {
  constructor(
    private jobScopeItem: JobScopeItem,
    private formatUnit: (value: number) => string,
  ) {
    makeObservable(this)
  }

  @computed
  get id() {
    return this.jobScopeItem.id
  }

  @computed
  get name() {
    return this.jobScopeItem.name
  }

  @computed
  get quantity() {
    return this.jobScopeItem.quantity
  }

  @computed
  get price() {
    return this.jobScopeItem.price || 0
  }

  @computed
  get displayPrice() {
    return this.formatUnit(this.price)
  }

  @computed
  get total() {
    return this.price * this.jobScopeItem.quantity
  }

  @computed
  get displayTotal() {
    return this.formatUnit(this.price * this.jobScopeItem.quantity)
  }
}

export class AppliedCouponViewModel {
  @observable coupon: Coupon | null = null

  constructor(
    private couponStore: CouponStore,
    private job: Job,
    private removeCouponAsAdminVM: RemoveCouponAsAdminViewModel,
    private fetchSummary: () => Promise<void>,
  ) {
    makeObservable(this)
  }

  /**
   * Coupon name
   */
  @computed
  get name() {
    return this.coupon?.friendlyName || this.coupon?.code
  }

  /**
   * Coupon description
   */
  @computed
  get description() {
    return this.coupon?.description
  }

  /**
   * Coupon discount
   */
  @computed
  get discount() {
    return this.coupon?.offType.type === 'AMOUNT_OFF'
      ? `${formatMoney(this.coupon.offType.amountOff)} OFF`
      : `${this.coupon?.offType.percentOff}% OFF`
  }

  /**
   * Opens the remove coupon as admin dialog
   */
  @action
  removeCoupon() {
    this.removeCouponAsAdminVM.show()
  }

  @action.bound
  async fetchCoupon() {
    this.coupon = await this.couponStore.fetchCouponByCode(
      this.job.couponCodes,
      this.job.workspace.id,
    )
  }

  /**
   * Activates the VM
   */
  activate = task.resolved(async () => {
    await this.fetchSummary()
    await this.fetchCoupon()
  })

  /**
   * Deactivates the VM.
   */
  async deactivate() {
    // If the VM is deactivate, it means the coupon has been removed
    // so we need to fetch the summary again.
    await this.fetchSummary()
  }
}

export default class JobCheckoutViewModel {
  /**
   * Manages an Rx subscription while the VM is active.
   */
  private subscription = new Subscription()

  @observable job = Job.none
  @observable summary: Summary | null = null
  @observable publishedLegend = PublishedLegend.none
  @observable jobStatus: JobStatus = 'UNDER_CONSTRUCTION'

  /**
   * Dialog for applying coupons as Admin.
   */
  @observable applyCouponAsAdminVM: ApplyCouponAsAdminViewModel | null = null

  /**
   * Dialog for remove coupons as Admin.
   */
  @observable removeCouponAsAdminVM: RemoveCouponAsAdminViewModel | null = null

  constructor(
    public id: Id,
    private summaryStore: SummaryStore,
    private jobStore: JobStore,
    private publishedLegendStore: PublishedLegendStore,
    private couponStore: CouponStore,
    private newAlert: AlertFactory,
  ) {
    makeObservable(this)
  }

  /**
   * Fetch the information necessary for the checkout.
   */
  fetchJobInfo = task.resolved(async () => {
    this.job = await this.jobStore.fetchJob(this.id)
    this.jobStatus = this.job.status

    if (this.checkoutCompleted) {
      return
    }

    this.publishedLegend = await this.publishedLegendStore.fetchLegendVersion(
      this.job.legendVersionId,
      SUPPORTED_ENGINE_VERSION,
    )

    this.subscription.add(
      this.job.statusChange$.subscribe((jobStatus: JobStatus) => {
        this.jobStatus = jobStatus
      }),
    )

    this.applyCouponAsAdminVM = new ApplyCouponAsAdminViewModel(
      this.job,
      this.jobStore,
      this.couponStore,
      this.newAlert,
    )

    this.removeCouponAsAdminVM = new RemoveCouponAsAdminViewModel(
      this.job,
      this.jobStore,
      this.couponStore,
      this.newAlert,
    )

    await this.fetchSummary()
  })

  @action.bound
  async refreshJob() {
    this.job = await this.jobStore.refreshJob(this.id)
  }

  /**
   * The champion client of the job.
   */
  @computed
  get championClient() {
    return this.job.championClient
  }

  /**
   * The job checkout link.
   */
  @computed
  get checkoutLink() {
    return `${Env.CLIENT_PORTAL_URL}/external_checkout/${this.job.id}`
  }

  /**
   * Job total.
   */
  @computed
  get total() {
    return this.summary?.pricing?.total || this.job.total
  }

  /**
   * The amount discounted from coupon.
   */
  @computed
  get discount() {
    return this.summary?.pricing?.discount
  }

  /**
   * Job price without discount.
   */
  @computed
  get primaryAmount() {
    return this.summary?.pricing?.primaryAmount || formatMoney(this.job.total)
  }

  /**
   *  Amount due upfront.
   */
  @computed
  get amountDueNow() {
    return (
      asNumber(
        // Remove any non-numeric characters except for a decimal
        this.summary?.pricing?.amountDueNow.replace(/[^0-9.]/g, ''),
      ) ?? 0
    )
  }

  /**
   * The discount label if applies.
   */
  @computed
  get discountLabel() {
    return `-${this.summary?.pricing?.discount}`
  }

  @computed
  get checkoutCompleted() {
    return this.jobStatus !== 'UNDER_CONSTRUCTION'
  }

  /**
   * Wether the job has coupons.
   */
  @computed
  get hasCoupons() {
    return Boolean(this.job.couponCodes)
  }

  /**
   * Wether the job can be removed from the checkout.
   */
  @computed
  get canRemoveJob() {
    return Boolean(new URLSearchParams(window.location.search).get('job_ids'))
  }

  @computed
  get quoteScopeItemsNames() {
    return this.summary?.jobScopeItemsNames || []
  }

  /**
   * List of JobScopeItemViewModel.
   */
  @computed
  get jobScopeItems() {
    const formatUnit =
      this.summary?.formatUnit || ((value: number) => value.toString())

    return this.job.jobScopeItems.map(
      (item) => new JobScopeItemViewModel(item, formatUnit),
    )
  }

  /**
   * Wether coupons are enable and the user can add them.
   */
  @computed
  get canAddCoupons() {
    return this.job !== Job.none && this.job.canManageCouponAsAdmin
  }

  /**
   * Fetch payment summary.
   */
  @action.bound
  async fetchSummary() {
    this.summary = await this.summaryStore.fetchSummary(
      this.job,
      this.publishedLegend,
    )
  }

  /**
   * Applied coupon vm
   */
  @computed
  get appliedCouponVM() {
    if (this.job.couponCodes && this.removeCouponAsAdminVM) {
      return new AppliedCouponViewModel(
        this.couponStore,
        this.job,
        this.removeCouponAsAdminVM,
        () => this.fetchSummary(),
      )
    }

    return null
  }

  /**
   * Open dialog to add coupons.
   */
  @action
  openCouponDialog() {
    if (this.canAddCoupons && this.applyCouponAsAdminVM !== null) {
      this.applyCouponAsAdminVM.show()
    }
  }

  /**
   * Submit the job with the selected payment method.
   * @returns
   */
  pay = task.resolved(async (paymentMethod: BillingMethod | null) => {
    if (this.checkoutCompleted) {
      return
    }

    // Submit the job with the selected payment method.
    await this.jobStore.submitRequestV3(this.job.id, {
      bypassBilling: false,
      paymentMethod,
    })
  })

  /**
   * Removes the job from the checkout.
   */
  remove() {
    const params = new URLSearchParams(window.location.search)
    const jobIds = params.get('job_ids')?.split(',')
    params.set(
      'job_ids',
      jobIds?.filter((id) => id !== this.id)?.join(',') ?? '',
    )
    window.location.search = params.toString()
  }

  deactivate() {
    this.subscription.unsubscribe()
  }
}
